import { useDispatch } from 'react-redux';
import { generatePath, useNavigate } from 'react-router';
import { SubscriptionType } from '../../generated/api/subscriptionType';
import { addNotification } from '../../public/site/notificationStore.js';
import { closeSuspendSubscriptionOptions } from '../../selfservice/actions';
import { pushToDataLayer } from '../analytics';
import { paths } from '../constants/pathVariables';
import { changeSubscriptionFeatures } from '../fetch';
import { t } from '../i18n';
import { getSubscriptionChangeAnalyticsEvent } from '../utils/analyticsUtils';
import { useSearchParams } from './useSearchParams';
import type { CampaignAssociation } from '../../generated/api/campaignAssociation';
import type { Offer } from '../../generated/api/offer';
import type { Subscription } from '../../generated/api/subscription';
import type { SubscriptionChangeFeaturesRequest } from '../../generated/api/subscriptionChangeFeaturesRequest';
import type { SubscriptionAddOnAttributesMap } from '../../selfservice/actions';

type PostChangeOffer = (
  subscription: Subscription,
  commercialProductCode: string,
  addOnsToAdd: string[],
  addOnsToRemove: string[],
  addOnsToUpdate: string[],
  campaignAssociation?: CampaignAssociation,
  addOnAttributes?: SubscriptionAddOnAttributesMap,
  changeFeePriceItemId?: string,
  onlineModelName?: string,
  offer?: Offer,
  previousOffer?: Offer,
  voucher?: string
) => Promise<void>;

export const useChangeOffer = (): PostChangeOffer => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mdmId } = useSearchParams<{ mdmId: string }>();

  return async (
    sub: Subscription,
    commercialProductCode: string,
    addOnsToAdd: string[],
    addOnsToRemove: string[],
    addOnsToUpdate: string[],
    campaignAssociation?: CampaignAssociation,
    addOnAttributes?: SubscriptionAddOnAttributesMap,
    changeFeePriceItemId?: string,
    onlineModelName?: string,
    offer?: Offer,
    previousOffer?: Offer,
    voucher?: string
  ) => {
    try {
      const payload: SubscriptionChangeFeaturesRequest = {
        commercialProductCode,
        addOnChanges: {
          addOnsToAdd: addOnsToAdd.map(addOnGuid => ({
            addOnCode: addOnGuid,
            addOnAttributes: addOnAttributes && addOnAttributes[addOnGuid],
          })),
          addOnsToRemove: addOnsToRemove.map(addOnGuid => ({ addOnCode: addOnGuid })),
          addOnsToUpdate: addOnsToUpdate?.map(addOnGuid => ({
            addOnCode: addOnGuid,
            addOnAttributes: addOnAttributes && addOnAttributes[addOnGuid],
          })),
        },
        campaignAssociationCode: campaignAssociation?.campaignAssociationCode,
        changeFeePriceItemId,
      };

      await changeSubscriptionFeatures(payload, sub.subscriptionId, mdmId);
      dispatch(closeSuspendSubscriptionOptions());
      addNotification(t.YZ8Q('Offer change was successful'));

      if (onlineModelName && offer) {
        pushToDataLayer(
          getSubscriptionChangeAnalyticsEvent(onlineModelName, sub, offer, previousOffer, campaignAssociation, voucher)
        );
      }

      const isMbb =
        sub.subscriptionType === SubscriptionType.MOBILE_BROADBAND ||
        sub.subscriptionType === SubscriptionType.MOBILE_M2M;
      const path = isMbb ? paths.PS_BROADBAND_SUBSCRIPTION : paths.PS_MOBILE_SUBSCRIPTION;
      navigate(`${generatePath(path, { subscriptionId: sub.subscriptionDisplayId })}${mdmId ? `?mdmId=${mdmId}` : ''}`);
    } catch {
      addNotification(t.D205('Offer change failed'), 'error');
    }
  };
};
